import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { PageContainer } from '@/components/layout/PageContainer';
import { TopBar } from '@/components/layout/TopBar';
import { Card } from '@/components/ui/Card';
import { Toggle } from '@/components/ui/Toggle';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useAutoBookingSettings, useUpdateAutoBookingSettings } from '@/api/queries/user';
import { useTriggerEventTypes } from '@/api/queries/reference';
import type { EvacuationTriggerCode } from '@/api/types';
import { useUIStore } from '@/store/ui';

export function SettingsEvacuationPage(): JSX.Element {
  const { t } = useTranslation();
  const settings = useAutoBookingSettings();
  const triggers = useTriggerEventTypes();
  const update = useUpdateAutoBookingSettings();
  const pushToast = useUIStore((s) => s.pushToast);

  const [selected, setSelected] = useState<EvacuationTriggerCode[]>([]);

  useEffect(() => {
    if (settings.data?.evacuation_triggers) setSelected(settings.data.evacuation_triggers);
  }, [settings.data]);

  const save = async () => {
    try {
      await update.mutateAsync({ evacuation_triggers: selected });
      pushToast({ body: t('common.done'), variant: 'success' });
    } catch {
      pushToast({ body: t('errors.generic'), variant: 'error' });
    }
  };

  return (
    <AppShell>
      <PageContainer>
        <TopBar back title={t('settings.evacuation_triggers.title')} />
        {triggers.isLoading && <Spinner />}
        <Card variant="light" padding="md">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(triggers.data ?? []).map((tr) => (
              <Toggle
                key={tr.code}
                label={t(`triggers.${tr.code}`, { defaultValue: tr.label_en })}
                checked={selected.includes(tr.code as EvacuationTriggerCode)}
                onChange={(e) => {
                  setSelected((cur) =>
                    e.target.checked
                      ? [...cur, tr.code as EvacuationTriggerCode]
                      : cur.filter((c) => c !== tr.code),
                  );
                }}
              />
            ))}
          </div>
        </Card>
        <Button block size="lg" variant="inverted" onClick={save} loading={update.isPending}>
          {t('common.save')}
        </Button>
      </PageContainer>
    </AppShell>
  );
}
